import React, { useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import maximize from 'assets/maximize.svg';
import minimize from 'assets/minimize.svg';
import getNextObjectKey from 'services/numbers/getNextObjectKey';
import InputLabel from 'components/layout/forms/InputLabel';
import LinkButton from 'components/layout/buttons/LinkButton';
import translations from 'services/translations/translations';
import { moveItem } from 'services/arrays/moveItem';
import { PositionUnitPriceHeader } from '../PositionUnitPrice';
import { SubproductHistoryRows } from '../PositionHistory';

import IDetail from '../../IDetail';
import ISubproduct from '../../ISubproduct';
import RowOrder from '../RowOrder';
import Subproduct from './Subproduct';
import getDefaultSubproduct from './getDefaultSubproduct';
import SubproductsSum from './SubproductsSum';

const Subproducts = ({
  locked,
  quantity,
  onRemoveDetail,
  onRemoveSubproduct,
  reordering,
  subproducts,
  setSubproducts,
  targetMarginPercent,
}: {
  locked: boolean;
  quantity: number;
  // Deleted rows are kept by the calculation window so they can be restored
  onRemoveDetail: (
    subproduct: ISubproduct,
    detail: IDetail,
    index: number
  ) => void;
  onRemoveSubproduct: (subproduct: ISubproduct, index: number) => void;
  reordering: boolean;
  subproducts: ISubproduct[];
  setSubproducts: (subproducts: ISubproduct[]) => void;
  // New rows follow the target margin % of the window
  targetMarginPercent: string;
}): JSX.Element => {
  const { t } = useTranslation([
    translations.common,
    translations.offers,
    translations.products,
  ]);

  // The rows whose details are open; several can be open at the same time
  const [openedSubproducts, setOpenedSubproducts] = useState<number[]>([]);

  const toggleSubproduct = (key: number) =>
    setOpenedSubproducts((opened) =>
      opened.includes(key)
        ? opened.filter((openedKey) => openedKey !== key)
        : [...opened, key]
    );

  const subproductsWithDetails = subproducts.filter(
    (subproduct) => subproduct.details.length > 0
  );
  const anyOpened = subproducts.some((subproduct) =>
    openedSubproducts.includes(subproduct.key)
  );

  // Closes all open details, or when none are open, opens the rows that have details
  const toggleAllSubproducts = () =>
    setOpenedSubproducts(
      anyOpened
        ? []
        : subproductsWithDetails.map((subproduct) => subproduct.key)
    );

  const toggleAllLabel = anyOpened
    ? t(`${translations.calculationWindow}:Close all details`)
    : t(`${translations.calculationWindow}:Open all details`);

  const addSubproduct = () => {
    const key = getNextObjectKey(subproducts, 'key');
    setSubproducts([
      ...subproducts,
      { ...getDefaultSubproduct(key), followsTargetMargin: true },
    ]);
  };

  const removeSubproduct = (subproduct: ISubproduct) => {
    onRemoveSubproduct(
      subproduct,
      subproducts.findIndex(
        (existingSubproduct) => existingSubproduct.key === subproduct.key
      )
    );
    setSubproducts(
      subproducts.filter(
        (existingSubproduct) => existingSubproduct.key !== subproduct.key
      )
    );
  };

  const setSubproduct = (subproduct: ISubproduct) =>
    setSubproducts(
      subproducts.map((existingSubproduct) =>
        existingSubproduct.key === subproduct.key
          ? subproduct
          : existingSubproduct
      )
    );

  const offerIsLocked = () => locked;

  const moveSubproduct = (fromIndex: number, toIndex: number) =>
    setSubproducts(moveItem(subproducts, fromIndex, toIndex));

  return (
    <>
      <RowOrder count={subproducts.length} reordering={reordering}>
        <Row className="subproduct-row">
          <Col>
            <InputLabel>{t(`${translations.common}:Category`)}</InputLabel>
          </Col>
          <Col>
            <InputLabel>{t(`${translations.products}:Product`)}</InputLabel>
          </Col>
          <Col className="icon-column">
            {anyOpened || subproductsWithDetails.length > 0 ? (
              <button
                aria-label={toggleAllLabel}
                className="toggle-all-details"
                onClick={toggleAllSubproducts}
                title={toggleAllLabel}
                type="button"
              >
                <img alt="" src={anyOpened ? minimize : maximize} />
              </button>
            ) : null}
          </Col>
          <Col>
            <InputLabel>{t(`${translations.common}:Quantity`)}</InputLabel>
          </Col>
          <Col>
            <InputLabel>{t(`${translations.offers}:Purchase`)}</InputLabel>
          </Col>
          <Col className="small-column">
            <InputLabel>{t(`${translations.offers}:Waste %`)}</InputLabel>
          </Col>
          <Col className="small-column">
            <InputLabel>{t(`${translations.offers}:Expense`)}</InputLabel>
          </Col>
          <Col>
            <InputLabel>{t(`${translations.offers}:Selling price`)}</InputLabel>
          </Col>
          <Col className="small-column">
            <PositionUnitPriceHeader />
          </Col>
          <Col className="small-column">
            <InputLabel>
              {`${t(`${translations.offers}:Contribution margin`)} €`}
            </InputLabel>
          </Col>
          <Col className="small-column">
            <InputLabel>
              {t(`${translations.calculationWindow}:Expense short`)}{' '}
              {t(`${translations.common}:total-short`)}
            </InputLabel>
          </Col>
          <Col className="small-column">
            <InputLabel>
              {t(`${translations.calculationWindow}:Selling short`)}{' '}
              {t(`${translations.common}:total-short`)}
            </InputLabel>
          </Col>
          <Col className="small-column">
            <InputLabel>
              {t(`${translations.offers}:Contribution margin`)}{' '}
              {t(`${translations.common}:total-short`)} €
            </InputLabel>
          </Col>
          <Col className="small-column">
            <InputLabel>
              {`${t(`${translations.offers}:Contribution margin`)} %`}
            </InputLabel>
          </Col>
          <Col className="icon-column" />
        </Row>
      </RowOrder>
      {subproducts.map((subproduct, index) => (
        <React.Fragment key={`subproduct-${subproduct.key}`}>
          <RowOrder
            count={subproducts.length}
            index={index}
            move={moveSubproduct}
            reordering={reordering}
          >
            <Subproduct
              locked={locked}
              opened={openedSubproducts.includes(subproduct.key)}
              quantity={quantity}
              onRemoveDetail={onRemoveDetail}
              reordering={reordering}
              removeSubproduct={removeSubproduct}
              setSubproduct={setSubproduct}
              subproduct={subproduct}
              targetMarginPercent={targetMarginPercent}
              toggleOpened={() => toggleSubproduct(subproduct.key)}
            />
          </RowOrder>
          {reordering ? null : (
            <SubproductHistoryRows
              subproduct={subproduct}
              subproducts={subproducts}
            />
          )}
        </React.Fragment>
      ))}
      {offerIsLocked() ? null : (
        <LinkButton onClick={addSubproduct}>
          {`+ ${t(`${translations.common}:Add row`)}`}
        </LinkButton>
      )}
      <SubproductsSum quantity={quantity} subproducts={subproducts} />
    </>
  );
};

export default Subproducts;
